import NumberFlow from "@number-flow/react"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"

interface MatchupTeam {
  name: string
  shortName: string
  logo: string
  score: number
  winPercentage: number
  trend: "up" | "down" | "neutral"
  color: string
}

interface BracketMatchupCardProps {
  homeTeam: MatchupTeam
  awayTeam: MatchupTeam
  period: string
  timeRemaining: string
  league: string
  isLive?: boolean
}

export function BracketMatchupCard({
  homeTeam,
  awayTeam,
  period,
  timeRemaining,
  league,
  isLive = true,
}: BracketMatchupCardProps) {
  // Add state for simulating percentage changes
  const [simulatedHomePercentage, setSimulatedHomePercentage] = useState(homeTeam.winPercentage)
  const [simulatedAwayPercentage, setSimulatedAwayPercentage] = useState(awayTeam.winPercentage)

  // Add state for caret animations
  const [homeTrend, setHomeTrend] = useState<"up" | "down" | "neutral">(homeTeam.trend)
  const [awayTrend, setAwayTrend] = useState<"up" | "down" | "neutral">(awayTeam.trend)

  // Simulate trade activity
  useEffect(() => {
    if (!isLive) return

    const simulationInterval = setInterval(() => {
      // Random percentage change between -4 and +4 (increased from -2 to +2)
      const homeChange = Math.random() * 8 - 4

      // Ensure percentages always sum to 100
      setSimulatedHomePercentage((prev) => {
        const newValue = Math.max(Math.min(prev + homeChange, 95), 5)
        setSimulatedAwayPercentage(100 - newValue)

        // Update trend indicators based on change
        setHomeTrend(homeChange > 0.2 ? "up" : homeChange < -0.2 ? "down" : "neutral")
        setAwayTrend(homeChange < -0.2 ? "up" : homeChange > 0.2 ? "down" : "neutral")

        return newValue
      })

      // Randomly flicker carets to simulate trade activity
      if (Math.random() > 0.7) {
        // Create a rapid flickering effect with multiple state changes
        const flickerCount = Math.floor(Math.random() * 3) + 2 // 2-4 flickers
        let flickersDone = 0

        const flickerInterval = setInterval(() => {
          // Randomly choose which direction to flicker
          const randomHomeTrend: "up" | "down" | "neutral" = Math.random() > 0.5 ? "up" : "down"
          const randomAwayTrend: "up" | "down" | "neutral" = randomHomeTrend === "up" ? "down" : "up"

          setHomeTrend(randomHomeTrend)
          setAwayTrend(randomAwayTrend)

          flickersDone++

          // After a few flickers, reset to neutral
          if (flickersDone >= flickerCount) {
            clearInterval(flickerInterval)
            setTimeout(() => {
              setHomeTrend("neutral")
              setAwayTrend("neutral")
            }, 50)
          }
        }, 100) // Flicker every 100ms
      }
    }, 1500)

    return () => clearInterval(simulationInterval)
  }, [isLive])

  // Helper function for caret rendering
  const renderCaret = (trend: "up" | "down" | "neutral", isUp: boolean) => {
    // Always show a white caret as the base
    const baseCaretClass = "text-white/10 text-[10px] absolute"

    // Determine if the colored caret should be visible based on trend
    const isVisible = (isUp && trend === "up") || (!isUp && trend === "down")
    const colorClass = isUp ? "text-[#00FF51]" : "text-[#FF0000]"

    return (
      <div className="relative h-2.5 flex items-center justify-center">
        {/* Permanent white caret */}
        <span className={baseCaretClass}>{isUp ? "▲" : "▼"}</span>

        {/* Colored caret that flickers */}
        {isVisible && (
          <motion.span
            className={`text-[8px] ${colorClass} absolute`}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.05 }}
          >
            {isUp ? "▲" : "▼"}
          </motion.span>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-[.9rem] mx-0 overflow-hidden bg-white/10 backdrop-blur-xs border border-white/5 mb-4">
      {/* Header */}
      <div className="relative flex justify-between items-center px-3 py-1 text-white/80">
        {/* Left side - Live indicator */}
        <div className="flex items-center">
          {isLive && (
            <>
              <div className="relative mr-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00FF51]" />
                <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-[#00FF51] animate-ping opacity-75" />
              </div>
              <span className="text-white/50 text-xs font-medium tracking-wide">LIVE</span>
            </>
          )}
        </div>

        {/* Absolutely centered time */}
        <div className="absolute left-1/2 transform -translate-x-1/2 text-center text-xs font-medium text-white/50">
          {period} {timeRemaining}
        </div>

        {/* Right side - League */}
        <div className="text-right text-xs font-medium text-white/50">{league}</div>
      </div>

      {/* Teams Container */}
      <div className="relative h-[92px] overflow-hidden">
        {/* Background Meters */}
        <div className="absolute inset-0 flex">
          {/* Away Team Meter - fills from left edge to percentage */}
          <div className="w-1/2 h-full relative overflow-hidden">
            <motion.div
              animate={{ width: `${Math.min((simulatedAwayPercentage / 100) * 90, 90) + 10}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute top-0 left-0 h-[150%] origin-left transform -skew-x-16"
              style={{
                backgroundColor: `${awayTeam.color}`,
                opacity: 0.75,
                left: "-15%", // Increased negative offset to fully cover left corner
              }}
            />
          </div>

          {/* Home Team Meter - fills from right edge to percentage */}
          <div className="w-1/2 h-full relative overflow-hidden">
            <motion.div
              animate={{ width: `${Math.min((simulatedHomePercentage / 100) * 90, 90) + 10}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute top-0 right-0 h-[150%] origin-right transform -skew-x-16"
              style={{
                backgroundColor: `${homeTeam.color}`,
                opacity: 0.75,
                right: "-15%", // Increased negative offset to fully cover right corner
              }}
            />
          </div>
        </div>

        {/* Center Divider - angled to match meters */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20 transform -translate-x-1/2 -skew-x-16 z-10" />

        {/* Content Layer */}
        <div className="absolute inset-0 flex">
          {/* Away Team */}
          <div className="w-1/2 p-3 flex flex-col justify-between relative z-10">
            {/* Away Team Content */}
            <div className="h-full flex flex-col justify-between">
              {/* Top Section */}
              <div className="flex justify-between items-start">
                {/* Logo on left */}
                <img src={awayTeam.logo} alt={awayTeam.name} className="w-10 h-10 object-contain" />

                {/* Percentage in center with trend indicators */}
                <div className="flex flex-col items-center">
                  <div className="mb-[-0.1rem]">{renderCaret(awayTrend, true)}</div>
                  <span className="text-white text-[1.6rem] font-bold flex items-center">
                    <NumberFlow
                      value={Math.round(simulatedAwayPercentage)}
                      format={{
                        style: "decimal",
                        useGrouping: false,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }}
                      transformTiming={{ duration: 300, easing: "ease" }}
                      trend={(oldValue, newValue) => (oldValue < newValue ? 1 : -1)}
                    />
                    <span className="ml-0.5">%</span>
                  </span>
                  <div className="mt-[-0.1rem]">{renderCaret(awayTrend, false)}</div>
                </div>

                {/* Empty space to balance layout */}
                <div className="w-10" />
              </div>

              {/* Bottom Section */}
              <div className="flex justify-between items-center">
                <span className="text-white/60 text-sm font-medium">1 {awayTeam.shortName}</span>
                <span className="text-white text-sm font-bold -translate-x-4">{awayTeam.score}</span>
              </div>
            </div>
          </div>

          {/* Home Team */}
          <div className="w-1/2 p-3 flex flex-col justify-between relative z-10">
            {/* Home Team Content */}
            <div className="h-full flex flex-col justify-between">
              {/* Top Section */}
              <div className="flex justify-between items-start">
                {/* Empty space to balance layout */}
                <div className="w-10" />

                {/* Percentage in center with trend indicators - shifted left by 1rem */}
                <div className="flex flex-col items-center mr-4">
                  <div className="mb-[-0.1rem]">{renderCaret(homeTrend, true)}</div>
                  <span className="text-white text-[1.6rem] font-bold flex items-center">
                    <NumberFlow
                      value={Math.round(simulatedHomePercentage)}
                      format={{
                        style: "decimal",
                        useGrouping: false,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }}
                      transformTiming={{ duration: 300, easing: "ease" }}
                      trend={(oldValue, newValue) => (oldValue < newValue ? 1 : -1)}
                    />
                    <span className="ml-0.5">%</span>
                  </span>
                  <div className="mt-[-0.1rem]">{renderCaret(homeTrend, false)}</div>
                </div>

                {/* Logo on right */}
                <img src={homeTeam.logo} alt={homeTeam.name} className="w-10 h-10 object-contain" />
              </div>

              {/* Bottom Section */}
              <div className="flex justify-between items-center">
                <span className="text-white text-sm font-bold">{homeTeam.score}</span>
                <span className="text-white/60 text-sm font-medium">8 {homeTeam.shortName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
